import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { Folder } from '../../types';

/**
 * Get the path of a folder by its id
 * Example: ['level1Folder.id', 'level2Folder.id', 'folderId']
 */
type GetFolderPathByIdArgs = {
  folders: Folder[];
  folderId: string;
};
export function getFolderPathById({ folders, folderId }: GetFolderPathByIdArgs): string[] | null {
  const findPath = (currentFolders: Folder[], targetId: string, path: string[]): string[] | null => {
    for (const folder of currentFolders) {
      path.push(`${folder.id}`);
      if (`${folder.id}` === targetId) {
        return path;
      }
      const subfolderPath = findPath(folder.subfolders || [], targetId, path);
      if (subfolderPath) {
        return subfolderPath;
      }
      path.pop();
    }
    return null;
  };

  return findPath(folders, folderId, []);
}

export function encodeFolderPath(folderPath: string[]): string {
  return btoa(JSON.stringify(folderPath));
}

export function decodeFolderPath(encodedFolderPath: string): string[] | null {
  try {
    return JSON.parse(atob(encodedFolderPath)) as string[];
  } catch (error) {
    console.error('Error decoding folder path');
    return null;
  }
}

type GetFolderObjectsPathByPathArgs = {
  foldersMap: Map<string, Folder>;
  folderPath: string[];
};
export const getFolderObjectsByPath = ({ foldersMap, folderPath }: GetFolderObjectsPathByPathArgs): Folder[] => {
  const folderObjects: Folder[] = [];

  for (const id of folderPath) {
    const folder = foldersMap.get(id);
    if (folder) {
      folderObjects.push(folder);
    } else {
      console.error(`Folder with id ${id} not found`);
      break;
    }
  }

  return folderObjects;
};

export function getMaxFoldersDepth(folders: Folder[]): number {
  const findMaxDepth = (currentFolders: Folder[], currentDepth: number): number => {
    let maxDepth = currentDepth;
    for (const folder of currentFolders) {
      const subfolderDepth = findMaxDepth(folder.subfolders || [], currentDepth + 1);
      maxDepth = Math.max(maxDepth, subfolderDepth);
    }
    return maxDepth;
  };

  return findMaxDepth(folders, 0);
}

export function getTreeViewItemsForSearch(folders: Folder[]): TreeViewBaseItem[] {
  const foldersForSearch: TreeViewBaseItem[] = [];

  const mapFolders = (currentFolders: Folder[], parentPath: string = '', level: number = 0) => {
    for (const folder of currentFolders) {
      let fullTitle: string[] = [];

      if (level === 0) {
        fullTitle = [folder.title];
      } else if (level === 1) {
        // One level deep: "topParent, currentFolder"
        fullTitle = [parentPath, folder.title];
      } else {
        // Two or more levels deep: "topParent, ..., currentFolder"
        fullTitle = [parentPath, '…', folder.title];
      }

      foldersForSearch.push({
        id: `${folder.id}`,
        label: fullTitle.join(' / '),
        children: []
      });

      if (folder.subfolders) {
        const nextParentPath = level === 0 ? folder.title : parentPath;
        mapFolders(folder.subfolders, nextParentPath, level + 1);
      }
    }
  };

  mapFolders(folders);

  return foldersForSearch;
}

export function convertFoldersToTreeViewItems(folders: Folder[]): TreeViewBaseItem[] {
  return folders.map(
    (folder): TreeViewBaseItem => ({
      id: `${folder.id}`,
      label: folder.title,
      children: convertFoldersToTreeViewItems(folder.subfolders || [])
    })
  );
}
